import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { bugsnagConfig } from '@rv-life/bugsnag';
import ReactGA from 'react-ga4';
import { type MFEs } from '@/components/AppContext';
import { ClientConfig } from '@/app/actions';
import pkg from '../../package.json';
import { flushPendingBugsnagReports } from './bugsnag';
import { setAnalyticsReady, getAnalyticsHostMode, seedPageLocationContext } from './analytics';

const SHORT_SHA_LENGTH = 7;

const getContainerVersion = (): string => process.env.NEXT_PUBLIC_APP_VERSION ?? pkg.version;

const readIdentityValue = (value: unknown): string | undefined =>
	typeof value === 'string' && value.length > 0 ? value : undefined;

const formatBuildIdentity = (commitSha: unknown, buildEnvironment: unknown): string => {
	const sha = readIdentityValue(commitSha);
	const environment = readIdentityValue(buildEnvironment);

	const parts: string[] = [];
	if (sha) parts.push(`sha ${sha.slice(0, SHORT_SHA_LENGTH)}`);
	if (environment) parts.push(environment);

	return parts.length > 0 ? ` (${parts.join(', ')})` : '';
};

export const initializeClientServices = (config: ClientConfig): void => {
	const { bugsnag, googleAnalytics } = config || {};
	const containerVersion = getContainerVersion();
	const buildIdentity = formatBuildIdentity(
		process.env.NEXT_PUBLIC_BUILD_COMMIT_SHA,
		bugsnag?.releaseStage
	);

	// eslint-disable-next-line no-console
	console.log(`Profile version ${containerVersion}${buildIdentity}`);

	if (bugsnag?.enabled) {
		Bugsnag.start({
			...bugsnagConfig(),
			apiKey: bugsnag.apiKey!,
			autoTrackSessions: false,
			plugins: [new BugsnagPluginReact()],
			releaseStage: bugsnag.releaseStage,
			appVersion: containerVersion
		});
		flushPendingBugsnagReports();
	}

	// Embedded, the app owns analytics: no gtag, and the web GA4 switch doesn't silence the relay.
	if (getAnalyticsHostMode() === 'relay_only') {
		setAnalyticsReady(true);
	} else if (googleAnalytics?.enabled) {
		// Before config: a page_location on config would pin auto-collected hits to this address.
		seedPageLocationContext();
		// send_page_view:false — the host emits its own page_view per route via trackPageView.
		ReactGA.initialize(googleAnalytics.measurementId!, {
			gtagOptions: { send_page_view: false }
		});
		setAnalyticsReady(true);
	}
};

export const createMfeRootElement = (id: MFEs) => {
	const rootElement = document.getElementById('rvlife-mfe-root');
	if (rootElement) {
		rootElement.innerHTML = '';
	}
	const mfeRootElement = document.createElement('div');
	mfeRootElement.id = `rvlife-${id}-mfe-root`;
	rootElement?.appendChild(mfeRootElement);
	return mfeRootElement;
};
